/**
 * Contexte applicatif global.
 * 
 * Gère la navigation par onglets, le cycle de vie d'une partie,
 * les notifications cross-onglets, les toasts et les préférences utilisateur.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { TabId, GameConnectionInfo, GameNotification, AppToast, ShowToastOptions, UserPreferences, TrainingInfo, LibraryNavigationRequest, DiffereNavigationRequest } from '../types/app';
import type { GameTable } from '../types/game';
import type { GameInvitePayload, MessageQuote } from '../types/chat';
import type { GlobalChatPanelApi } from '../components/chat/GlobalChatPanelApi';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useTheme } from '../hooks/useTheme';
import { writeLibraryDeepLink } from '../utils/libraryDeepLink';

// ============================================
// Types du contexte
// ============================================

/** Utilisateur connecté (tel que reçu du chat global) */
export interface ConnectedUserInfo {
  pseudo: string;
  pseudoDisplay: string;
  source: 'app' | 'irc';
  lastSeen: number;
}

/** Message du chat global (exposé via le contexte pour Chat + Home) */
export interface GlobalChatMessage {
  roomId: string;
  pseudo: string;
  text: string;
  timestamp: number;
  source?: 'app' | 'irc';
  messageId?: string;
  quote?: MessageQuote;
  messageType?: 'text' | 'game_invite';
  gameInvite?: GameInvitePayload;
  reactions?: import('../types/chat').MessageReaction[];
}

/** Catégorie de tables triée (exposée via le contexte) */
export interface TableCategory {
  name: string;
  originalName: string;
  tables: GameTable[];
  playerCount: number;
}

interface AppContextValue {
  // --- Navigation ---
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  /** Retape de l'onglet déjà actif → les pages drill-down reviennent à l'écran racine. */
  subscribeTabReselect: (tab: TabId, fn: () => void) => () => void;

  // --- Cycle de vie de la partie ---
  gameInfo: GameConnectionInfo | null;
  startGame: (info: GameConnectionInfo) => void;
  updateGameInfo: (partial: Partial<GameConnectionInfo>) => void;
  endGame: () => void;

  // --- Mode entraînement ---
  trainingInfo: TrainingInfo | null;
  startTraining: (info: TrainingInfo) => void;
  stopTraining: () => void;
  updateTraining: (partial: Partial<TrainingInfo>) => void;
  /** Colonne gauche Entraînement masquée (layout large uniquement) — sync Nav + page */
  trainingLeftColumnHidden: boolean;
  setTrainingLeftColumnHidden: (hidden: boolean) => void;
  /** Tapis d'entraînement affiché (pas l'accueil). Sert au chrome immersif paysage. */
  trainingFeltActive: boolean;
  setTrainingFeltActive: (active: boolean) => void;

  // --- Notifications (badges) ---
  chatUnreadCount: number;
  setChatUnreadCount: (count: number) => void;
  gameNotification: GameNotification | null;
  setGameNotification: (notif: GameNotification | null) => void;
  /** Invitations draft en attente (toast actif) */
  invitationCount: number;
  setInvitationCount: (count: number) => void;

  // --- Toasts ---
  toasts: AppToast[];
  showToast: (toast: Omit<AppToast, 'id'>, options?: ShowToastOptions) => void;
  dismissToast: (id: string) => void;

  // --- Préférences ---
  preferences: UserPreferences;
  updatePreferences: (partial: Partial<UserPreferences>) => void;

  // --- Joueurs connectés et en jeu (pour invitations draft) ---
  connectedUsers: ConnectedUserInfo[];
  setConnectedUsers: (users: ConnectedUserInfo[]) => void;
  playingUsers: Set<string>;
  setPlayingUsers: (users: Set<string>) => void;

  /**
   * Clés (`ip:port`) des tables de défi legacy où l'utilisateur courant est
   * attendu mais pas encore connecté (alimenté par useNotificationSocket via
   * MainLayout, consommé par Tables.tsx pour la mise en évidence).
   */
  expectedDefiTableKeys: Set<string>;
  addExpectedDefiTable: (tableKey: string) => void;
  removeExpectedDefiTable: (tableKey: string) => void;

  // --- Données partagées pour la page Home ---
  globalMessages: GlobalChatMessage[];
  setGlobalMessages: (msgs: GlobalChatMessage[]) => void;
  globalChatPanel: GlobalChatPanelApi | null;
  setGlobalChatPanel: (panel: GlobalChatPanelApi | null) => void;
  sendGlobalMessage: ((text: string) => void) | null;
  setSendGlobalMessage: (fn: ((text: string) => void) | null) => void;
  chatConnected: boolean;
  setChatConnected: (connected: boolean) => void;
  playerTeams: Record<string, string | null>;
  setPlayerTeams: (teams: Record<string, string | null>) => void;
  tablesCategories: TableCategory[];
  setTablesCategories: (cats: TableCategory[]) => void;
  joinTable: ((table: GameTable) => void) | null;
  setJoinTable: (fn: ((table: GameTable) => void) | null) => void;

  /** Ouvre le hub différé rétro (enregistré par Tables.tsx). */
  openRetroDiffere: (() => void) | null;
  setOpenRetroDiffere: (fn: (() => void) | null) => void;

  /** Version du cache avatar (incrémentée après upload) */
  avatarVersion: number;
  bumpAvatarVersion: () => void;

  /** Badge bibliothèque — partages reçus non consultés */
  libraryNewCount: number;
  setLibraryNewCount: (count: number) => void;
  /** Sondages en attente de vote (accessible) */
  libraryPendingPollCount: number;
  setLibraryPendingPollCount: (count: number) => void;

  /** Navigation profonde vers la bibliothèque (DM, liens internes) */
  libraryNavigationRequest: LibraryNavigationRequest | null;
  navigateToLibrary: (request?: LibraryNavigationRequest) => void;
  clearLibraryNavigationRequest: () => void;

  /** Navigation profonde vers les résultats différé */
  differeNavigationRequest: DiffereNavigationRequest | null;
  navigateToDiffere: (request: DiffereNavigationRequest) => void;
  clearDiffereNavigationRequest: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

// ============================================
// Hook d'accès au contexte
// ============================================

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useApp() doit être utilisé à l\'intérieur de <AppProvider>');
  }
  return ctx;
}

// ============================================
// Provider
// ============================================

interface AppProviderProps {
  children: React.ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  // --- Navigation ---
  const [activeTab, setActiveTabRaw] = useState<TabId>('home');
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const tabReselectListenersRef = useRef(new Map<TabId, Set<() => void>>());

  const subscribeTabReselect = useCallback((tab: TabId, fn: () => void) => {
    let listeners = tabReselectListenersRef.current.get(tab);
    if (!listeners) {
      listeners = new Set();
      tabReselectListenersRef.current.set(tab, listeners);
    }
    listeners.add(fn);
    return () => {
      listeners!.delete(fn);
    };
  }, []);

  const setActiveTab = useCallback((tab: TabId) => {
    if (activeTabRef.current === tab) {
      tabReselectListenersRef.current.get(tab)?.forEach((fn) => fn());
      return;
    }
    setActiveTabRaw(tab);
  }, []);

  // --- Cycle de vie de la partie ---
  const [gameInfo, setGameInfo] = useState<GameConnectionInfo | null>(null);

  const startGame = useCallback((info: GameConnectionInfo) => {
    setGameInfo(info);
    setActiveTabRaw('game');
    setGameNotification(null);
  }, []);

  const updateGameInfo = useCallback((partial: Partial<GameConnectionInfo>) => {
    setGameInfo(prev => prev ? { ...prev, ...partial } : prev);
  }, []);

  const endGame = useCallback(() => {
    setGameInfo(null);
    setGameNotification(null);
    setActiveTabRaw('tables');
  }, []);

  // --- Mode entraînement ---
  const [trainingInfo, setTrainingInfo] = useState<TrainingInfo | null>(null);

  const startTraining = useCallback((info: TrainingInfo) => {
    setTrainingInfo(info);
    setActiveTabRaw('training');
  }, []);

  const stopTraining = useCallback(() => {
    setTrainingInfo(null);
    setActiveTabRaw('training');
  }, []);

  const updateTraining = useCallback((partial: Partial<TrainingInfo>) => {
    setTrainingInfo(prev => prev ? { ...prev, ...partial } : null);
  }, []);

  const [trainingLeftColumnHidden, setTrainingLeftColumnHidden] = useState(false);
  const [trainingFeltActive, setTrainingFeltActive] = useState(false);

  // --- Notifications (badges) ---
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [gameNotification, setGameNotification] = useState<GameNotification | null>(null);
  const [invitationCount, setInvitationCount] = useState(0);

  // --- Toasts ---
  const [toasts, setToasts] = useState<AppToast[]>([]);
  const toastIdCounter = useRef(0);

  const showToast = useCallback((toast: Omit<AppToast, 'id'>, options?: ShowToastOptions) => {
    if (options?.sourceTab && activeTabRef.current === options.sourceTab) {
      return;
    }

    const id = `app-toast-${Date.now()}-${++toastIdCounter.current}`;
    const newToast: AppToast = { ...toast, id };
    setToasts(prev => [...prev, newToast]);

    const duration = toast.duration ?? 5000;
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // --- Préférences ---
  const { preferences, updatePreferences } = useUserPreferences();
  useTheme(preferences.theme);

  // --- Joueurs connectés et en jeu ---
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUserInfo[]>([]);
  const [playingUsersRaw, setPlayingUsers] = useState<Set<string>>(new Set());
  const playingUsers = useMemo(() => playingUsersRaw, [playingUsersRaw]);

  const [expectedDefiTableKeys, setExpectedDefiTableKeys] = useState<Set<string>>(new Set());
  const addExpectedDefiTable = useCallback((tableKey: string) => {
    setExpectedDefiTableKeys(prev => {
      if (prev.has(tableKey)) return prev;
      const next = new Set(prev);
      next.add(tableKey);
      return next;
    });
  }, []);
  const removeExpectedDefiTable = useCallback((tableKey: string) => {
    setExpectedDefiTableKeys(prev => {
      if (!prev.has(tableKey)) return prev;
      const next = new Set(prev);
      next.delete(tableKey);
      return next;
    });
  }, []);

  // --- Données partagées pour la page Home ---
  const [globalMessages, setGlobalMessages] = useState<GlobalChatMessage[]>([]);
  const globalChatPanelRef = useRef<GlobalChatPanelApi | null>(null);
  const [globalChatPanelState, setGlobalChatPanelState] = useState<GlobalChatPanelApi | null>(null);
  const setGlobalChatPanel = useCallback((panel: GlobalChatPanelApi | null) => {
    globalChatPanelRef.current = panel;
    setGlobalChatPanelState(() => panel);
  }, []);
  const sendGlobalMessageRef = useRef<((text: string) => void) | null>(null);
  const [sendGlobalMessageState, setSendGlobalMessageState] = useState<((text: string) => void) | null>(null);
  const setSendGlobalMessage = useCallback((fn: ((text: string) => void) | null) => {
    sendGlobalMessageRef.current = fn;
    setSendGlobalMessageState(() => fn);
  }, []);
  const [chatConnected, setChatConnected] = useState(false);
  const [playerTeams, setPlayerTeams] = useState<Record<string, string | null>>({});
  const [tablesCategories, setTablesCategories] = useState<TableCategory[]>([]);
  const joinTableRef = useRef<((table: GameTable) => void) | null>(null);
  const [joinTableState, setJoinTableState] = useState<((table: GameTable) => void) | null>(null);
  const setJoinTable = useCallback((fn: ((table: GameTable) => void) | null) => {
    joinTableRef.current = fn;
    setJoinTableState(() => fn);
  }, []);

  const openRetroDiffereRef = useRef<(() => void) | null>(null);
  const [openRetroDiffereState, setOpenRetroDiffereState] = useState<(() => void) | null>(null);
  const setOpenRetroDiffere = useCallback((fn: (() => void) | null) => {
    openRetroDiffereRef.current = fn;
    setOpenRetroDiffereState(() => fn);
  }, []);

  const [avatarVersion, setAvatarVersion] = useState(0);
  const bumpAvatarVersion = useCallback(() => {
    setAvatarVersion(v => v + 1);
  }, []);

  const [libraryNewCount, setLibraryNewCount] = useState(0);
  const [libraryPendingPollCount, setLibraryPendingPollCount] = useState(0);

  const [libraryNavigationRequest, setLibraryNavigationRequest] = useState<LibraryNavigationRequest | null>(null);
  const navigateToLibrary = useCallback((request: LibraryNavigationRequest = {}) => {
    writeLibraryDeepLink(request);
    setLibraryNavigationRequest(request);
    setActiveTabRaw('library');
  }, []);
  const clearLibraryNavigationRequest = useCallback(() => {
    setLibraryNavigationRequest(null);
  }, []);

  const [differeNavigationRequest, setDiffereNavigationRequest] = useState<DiffereNavigationRequest | null>(null);
  const navigateToDiffere = useCallback((request: DiffereNavigationRequest) => {
    setDiffereNavigationRequest(request);
    setActiveTabRaw('competitions');
  }, []);
  const clearDiffereNavigationRequest = useCallback(() => {
    setDiffereNavigationRequest(null);
  }, []);

  // --- Auto-switch vers le jeu quand c'est à nous de jouer ---
  useEffect(() => {
    if (
      gameNotification?.type === 'your_turn' &&
      preferences.autoSwitchOnTurn &&
      activeTabRef.current !== 'game' &&
      gameInfo !== null
    ) {
      setActiveTabRaw('game');
    }
  }, [gameNotification, preferences.autoSwitchOnTurn, gameInfo]);

  // --- Valeur du contexte ---
  const value: AppContextValue = {
    activeTab,
    setActiveTab,
    subscribeTabReselect,
    gameInfo,
    startGame,
    updateGameInfo,
    endGame,
    trainingInfo,
    startTraining,
    stopTraining,
    updateTraining,
    trainingLeftColumnHidden,
    setTrainingLeftColumnHidden,
    trainingFeltActive,
    setTrainingFeltActive,
    chatUnreadCount,
    setChatUnreadCount,
    gameNotification,
    setGameNotification,
    invitationCount,
    setInvitationCount,
    toasts,
    showToast,
    dismissToast,
    preferences,
    updatePreferences,
    connectedUsers,
    setConnectedUsers,
    playingUsers,
    setPlayingUsers,
    expectedDefiTableKeys,
    addExpectedDefiTable,
    removeExpectedDefiTable,
    globalMessages,
    setGlobalMessages,
    globalChatPanel: globalChatPanelState,
    setGlobalChatPanel,
    sendGlobalMessage: sendGlobalMessageState,
    setSendGlobalMessage,
    chatConnected,
    setChatConnected,
    playerTeams,
    setPlayerTeams,
    tablesCategories,
    setTablesCategories,
    joinTable: joinTableState,
    setJoinTable,
    openRetroDiffere: openRetroDiffereState,
    setOpenRetroDiffere,
    avatarVersion,
    bumpAvatarVersion,
    libraryNewCount,
    setLibraryNewCount,
    libraryPendingPollCount,
    setLibraryPendingPollCount,
    libraryNavigationRequest,
    navigateToLibrary,
    clearLibraryNavigationRequest,
    differeNavigationRequest,
    navigateToDiffere,
    clearDiffereNavigationRequest,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
